import { useState, useEffect, useRef, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Popover, Dialog } from "@headlessui/react";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";

import { getAlerts } from "redux/slices/getAlertsSlice";
import { setUser } from "redux/slices/userSlice";
import { getAgent } from "redux/agent/slices/getAgentSlice";
import { agentSwitchAgentId } from "service/userservice/agentSwitchAgentId";
import { useAuthorizationCheck } from "hooks/useAuthorizationCheck";
import { useMobileHeader } from "hooks/useMobileHeader";
import { getImage, isFlagEnabled } from "js/utilities";
import { convertToTitleCase } from "js/formatting";
import { AgentData } from "context/Agent/DataProvider";
import { getPolicies } from "service/agent/getPolicies";

import NavLink from "components/General/NavLink";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";

import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import config from "config/config";

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mobileMenuButton = useRef(null);

  const { csrUser } = useSelector((s) => s.csrUser);
  const { user } = useSelector((s) => s.user);
  const { open: openMobileMenuNav, close: closeMobileMenuNav } =
    useMobileHeader();
  const { setAgentData, setIsAgentPoliciesLoading } = useContext(AgentData);

  const [currentPage, setCurrentPage] = useState("/");
  const [agentIdDialog, setAgentIdDialog] = useState(false);
  const [fetchAgentDataLoading, setFetchAgentDataLoading] = useState(false);
  const { register, handleSubmit } = useForm();

  const changeAgentId = async (data) => {
    // get token from user object from redux store.
    let token = user.token;
    setFetchAgentDataLoading(true);
    const response = await agentSwitchAgentId({
      ...data,
      alternateId: user.alternateAgentID,
      userId: user.userId,
    });

    // deconstructing and reassigning for when agent switches agent profile using the agent ID button, need to update token on rexux store.
    let { token: _, ...updatedUser } = response;
    updatedUser.token = token;
    dispatch(setUser(updatedUser));

    let policyIds = [];

    for (let i = 0; i < response?.agentHierarchy?.length; i++) {
      if (response?.agentHierarchy[i]?.policyIds)
        policyIds.push(...response?.agentHierarchy[i].policyIds);
    }
    setIsAgentPoliciesLoading(true);
    getPolicies(policyIds)
      .then((response) => {
        setAgentData({
          agentPolicies: response?.agentPolicies,
          filteredPolicies: response?.filteredPolicies,
          agentClaims: response?.agentClaims,
        });
        setIsAgentPoliciesLoading(false);
      })
      .catch((error) => {
        setIsAgentPoliciesLoading(false);
        console.error(error);
      });

    dispatch(getAlerts(policyIds.slice(0, 5)));
    dispatch(getAgent({ agentId: response.agentViewingId, updateStore: true }));

    setAgentIdDialog(false);
    if (response?.meta?.requestStatus === "fulfilled") {
      setFetchAgentDataLoading(false);
      navigate("/dashboard");
    } else {
      setFetchAgentDataLoading(false);
      console.error("Something wrong.", response?.payload?.status);
    }
  };

  useEffect(() => {
    setCurrentPage(location.pathname);
  }, [location.pathname]);

  return (
    <>
      <header className="lg:bg-white shadow-drop lg:sticky lg:top-0 lg:z-20">
        {!csrUser?.userId && (
          <a className="ui-skip-to-content" href="#mainContent">
            {t("UnAuthCustomerNavbar-skip-content")}
          </a>
        )}

        <div id="id_header">
          <div id="id_headerTopBar">
            <div id="id_utilityMenu" className="ui-utilitybar">
              <div className="container py-4">
                <div className="flex justify-between flex-row-reverse items-center lg:grid lg:gap-x-2 lg:grid-cols-[1fr_max-content_max-content_max-content_max-content] lg:items-center">
                  <div className="lg:flex lg:items-center">
                    <div className="lg:flex">
                      <div id="everlakeLogoHeader">
                        <Link
                          to="/dashboard"
                          title={t("AuthHeader-return-to-header")}
                          onClick={() => setCurrentPage((page) => (page = "/"))}
                        >
                          <img
                            src={getImage("logo_blue.png")}
                            className="ui-logo-header"
                            alt={`${convertToTitleCase(
                              config.COMPANY_NAME
                            )} Logo`}
                          />
                        </Link>
                      </div>
                      <div id="lumicoLogoHeader">
                        <Link
                          to="/dashboard"
                          title={t("AuthHeader-return-to-header")}
                          onClick={() => setCurrentPage((page) => (page = "/"))}
                        >
                          <img
                            src={getImage("image6.png")}
                            className="ui-logo-header"
                            alt={t("AuthHeader-alt-text-1")}
                          />
                        </Link>
                      </div>
                    </div>
                    <div className="hidden lg:flex text-[color:var(--theme-color-portal-title)] font-semibold ml-6 px-6 border-l border-theme-rule">
                      {t("AuthHeader-title")}
                    </div>
                  </div>

                  <button
                    aria-label={t("AuthHeader-aria-label-1")}
                    title={t("AuthHeader-button-1")}
                    className={`relative
                    ${
                      currentPage.split("/")[1] === "profile"
                        ? "ui-current"
                        : ""
                    }
                  `}
                    onClick={() => {
                      navigate("/profile");
                    }}
                  >
                    <span className="ui-btn-icon group w-7 h-7 rounded-full overflow-hidden hidden lg:flex lg:items-center lg:justify-center">
                      <svg
                        className="ui-icon w-4 h-4"
                        aria-hidden="true"
                        role="presentation"
                      >
                        <use href="#ui-icon-profile"></use>
                      </svg>
                    </span>
                  </button>

                  <button
                    aria-label={t("AuthHeader-button-2")}
                    title={t("AuthHeader-button-2")}
                    className="ui-btn-icon h-7 w-7 rounded-full hidden lg:flex lg:items-center lg:justify-center"
                    onClick={() => {
                      navigate("/");
                    }}
                  >
                    <svg
                      className="ui-icon w-4 h-4"
                      aria-hidden="true"
                      role="presentation"
                    >
                      <use href="#ui-icon-signout"></use>
                    </svg>
                  </button>

                  <button
                    id="id_menuBtn"
                    ref={mobileMenuButton}
                    onClick={openMobileMenuNav}
                    aria-label={t("AuthHeader-aria-label-2")}
                    aria-controls="id_mainMenu"
                    className="ui-btn-icon h-8 w-8 flex items-center justify-center rounded-full m-0 lg:hidden"
                  >
                    <svg
                      className="ui-icon w-5 h-4"
                      aria-hidden="true"
                      role="presentation"
                    >
                      <use href="#ui-icon-menu"></use>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div id="id_mainMenu" className="" role="menu">
            <div className="ui-navbar w-64 lg:flex lg:w-full lg:z-auto lg:relative">
              <div
                id="id_mobileMenuMask"
                className="hidden fixed inset-0 w-full h-full bg-ui-gray-dark opacity-40 z-[800] lg:hidden lg:inset-auto lg:z-auto"
              ></div>

              <nav
                aria-label={t("AuthHeader-aria-label-2")}
                className="ui-main-menu hidden absolute w-64 z-[802] bottom-0 bg-white lg:flex lg:justify-between lg:relative lg:w-full lg:z-auto lg:inset-auto lg:bg-transparent lg:container"
              >
                <div className="flex flex-col justify-between h-full">
                  <ul className="ui-disclosure-nav flex flex-col p-0 m-0 left-0 border-t border-ui-gray-medium-light lg:flex-row lg:left-auto lg:border-0 lg:relative">
                    {useAuthorizationCheck(13) && (
                      <>
                        <li>
                          <NavLink
                            mainLink={true}
                            title={t("AuthHeader-nav-link-1")}
                            url="/dashboard"
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            closeMobileMenu={closeMobileMenuNav}
                          />
                        </li>
                      </>
                    )}
                    {useAuthorizationCheck(14) &&
                      (isFlagEnabled("ENABLE_SERVICE_TRACKING") ? (
                        <>
                          <Popover as="li" className="group">
                            {({ close }) => (
                              <>
                                <Popover.Button
                                  className={
                                    ["policies", "services", "claims"].includes(
                                      currentPage.split("/")[2]
                                    )
                                      ? "ui-current"
                                      : ""
                                  }
                                >
                                  {t("AgentHeader-contracts")}
                                </Popover.Button>

                                <Popover.Panel
                                  as="ul"
                                  className="bg-white border-b lg:shadow lg:rounded lg:border-ui-gray-medium-light"
                                >
                                  <li
                                    role="presentation"
                                    aria-hidden="true"
                                    className="ui-arrow"
                                  ></li>
                                  <li>
                                    <NavLink
                                      title={t("AuthHeader-nav-link-9")}
                                      url="/book-of-business"
                                      currentPage={currentPage}
                                      setCurrentPage={setCurrentPage}
                                      closeMenu={close}
                                      closeMobileMenu={closeMobileMenuNav}
                                    />
                                  </li>
                                  <li>
                                    <NavLink
                                      title={t("AuthHeader-nav-link-21")}
                                      url="/services"
                                      currentPage={currentPage}
                                      setCurrentPage={setCurrentPage}
                                      closeMenu={close}
                                      closeMobileMenu={closeMobileMenuNav}
                                    />
                                  </li>
                                  <li>
                                    <NavLink
                                      title={t("AuthHeader-nav-link-18")}
                                      url="/claims"
                                      currentPage={currentPage}
                                      setCurrentPage={setCurrentPage}
                                      closeMenu={close}
                                      closeMobileMenu={closeMobileMenuNav}
                                    />
                                  </li>
                                </Popover.Panel>
                              </>
                            )}
                          </Popover>
                        </>
                      ) : (
                        <li>
                          <NavLink
                            mainLink={true}
                            includeUrl="services-claims"
                            title={t("AuthHeader-nav-link-2")}
                            url="/services-claims"
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            closeMobileMenu={closeMobileMenuNav}
                          />
                        </li>
                      ))}
                    <li>
                      <NavLink
                        mainLink={true}
                        title={t("AuthHeader-nav-link-3")}
                        url="/commissions"
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        closeMobileMenu={closeMobileMenuNav}
                      />
                    </li>
                    {useAuthorizationCheck(15) && (
                      <>
                        <li>
                          <NavLink
                            mainLink={true}
                            title={t("AuthHeader-nav-link-24")}
                            url="/messages"
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            closeMobileMenu={closeMobileMenuNav}
                          />
                        </li>
                      </>
                    )}
                    <Popover as="li" className="group">
                      {({ close }) => (
                        <>
                          <Popover.Button
                            className={
                              currentPage.split("/")[1] === "help"
                                ? "ui-current"
                                : ""
                            }
                          >
                            {t("AuthHeader-button-3")}
                          </Popover.Button>

                          <Popover.Panel
                            as="ul"
                            className="bg-white border-b lg:shadow lg:rounded lg:border-ui-gray-medium-light"
                          >
                            <li
                              role="presentation"
                              aria-hidden="true"
                              className="ui-arrow"
                            ></li>
                            <li>
                              <NavLink
                                title={t("AuthHeader-nav-link-5")}
                                url="/help"
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                closeMenu={close}
                                closeMobileMenu={closeMobileMenuNav}
                              />
                            </li>
                            <li>
                              <NavLink
                                title={t("AuthHeader-nav-link-6")}
                                url="/help/faqs"
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                closeMenu={close}
                                closeMobileMenu={closeMobileMenuNav}
                              />
                            </li>
                          </Popover.Panel>
                        </>
                      )}
                    </Popover>
                  </ul>

                  <div className="mt-8 lg:hidden">
                    <ul className="ui-disclosure-nav">
                      <li>
                        <button
                          className="hidden lg:block bg-white text-ui-blue-dark leading-6 self-center px-8 text-sm rounded hover:ring hover:underline"
                          onClick={() => setAgentIdDialog(true)}
                        >
                          {t("AuthHeader-button-4")}
                        </button>
                      </li>
                      <li>
                        <Link to="/profile" className="ui-main-link">
                          <div className="w-6 h-6 rounded-full overflow-hidden mr-2">
                            <img
                              src={getImage("image11.png")}
                              alt="avatar"
                              className="w-6 h-auto"
                            />
                          </div>
                          {t("AuthHeader-nav-link-7")}
                        </Link>
                      </li>
                      <li
                        onKeyDown={(e) => {
                          if (
                            e.key === "Tab" &&
                            localStorage.getItem("isMobileHeaderMenuOpen") &&
                            localStorage.getItem("isMobileHeaderMenuOpen") ===
                              "true"
                          ) {
                            setTimeout(() => {
                              mobileMenuButton.current.focus();
                            }, 5);
                          }
                        }}
                      >
                        <a
                          href="/"
                          className="ui-main-link"
                          onClick={() => {
                            navigate("/");
                          }}
                        >
                          <svg
                            className="ui-icon w-4.5 h-4.5 text-ui-blue mr-2"
                            focusable="false"
                            aria-hidden="true"
                            role="presentation"
                          >
                            <use href="#ui-icon-signout"></use>
                          </svg>
                          {t("AuthHeader-nav-link-8")}
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>

                {user.associatedAgentIds?.length > 1 && (
                  <button
                    className="hidden lg:block bg-white text-ui-blue-dark leading-6 self-center px-8 text-sm rounded hover:ring hover:underline"
                    onClick={() => setAgentIdDialog(true)}
                  >
                    {t("AuthHeader-button-4")}
                  </button>
                )}
              </nav>
            </div>
          </div>
        </div>
      </header>

      <Dialog open={agentIdDialog} onClose={() => null} className="ui-dialogs">
        <DialogBackdrop />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog"
          >
            <Dialog.Title className="font-body text-xl font-bold text-left">
              {t("AuthHeader-button-4")}
            </Dialog.Title>
            <hr />
            <EverlakeLoadingOverlay
              isLoading={fetchAgentDataLoading}
              loadingText={t("AuthHeader-loading-text")}
            >
              <Dialog.Description>
                <Trans
                  i18nKey="AuthHeader-dialog-desc-1"
                  components={{ break: <br /> }}
                />
              </Dialog.Description>

              <form onSubmit={handleSubmit(changeAgentId)}>
                <label htmlFor="agent-id-selection" className="block mb-1">
                  {t("AuthHeader-label-1")}
                </label>
                <select
                  id="agent-id-selection"
                  defaultValue={user.agentViewingId}
                  {...register("agentId", {
                    require: t("AuthHeader-error-1"),
                  })}
                >
                  {user?.associatedAgentIds?.map((id) => (
                    <option
                      key={`agent id - ${id?.agentId}`}
                      value={id?.agentId}
                    >
                      {id?.alternateId}{" "}
                      {id?.agentId === user.agentID && "(Default)"}
                    </option>
                  ))}
                </select>

                <label className="mt-6 ui-checkbox text-sm font-normal">
                  <input type="checkbox" {...register("makeDefault")} />
                  <div className="ui-checkmark">
                    <svg
                      className="ui-icon w-2 h-2 text-white"
                      focusable="false"
                      aria-hidden="true"
                      role="presentation"
                    >
                      <use href="#ui-icon-checkmark"></use>
                    </svg>
                  </div>
                  <span>{t("AuthHeader-label-2")}</span>
                </label>

                <div className="block text-center mt-6">
                  <button
                    className="ui-btn-secondary min-w-[140px] mt-4"
                    aria-label={t("AuthHeader-aria-label-3")}
                    onClick={() => setAgentIdDialog(false)}
                  >
                    {t("AuthHeader-button-5")}
                  </button>
                  <button
                    className="ui-btn-primary min-w-[140px] mt-4"
                    aria-label={t("AuthHeader-aria-label-4")}
                  >
                    {t("AuthHeader-button-6")}
                  </button>
                </div>
              </form>
            </EverlakeLoadingOverlay>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
